import { AuthenticationState } from '@b2b/common/services/auth/authentication.service';
import { defer, filter, map, Observable } from 'rxjs';
import { PendoEvents } from './pendo.machine';

/**
 * Create an observable that transforms auth state changes into machine events
 */
export function createAuthStateObservable(authenticationState$: Observable<AuthenticationState>) {
  return defer(() => {
    let prev: AuthenticationState | null = null;

    return authenticationState$.pipe(
      map((curr): PendoEvents | null => {
        const event = mapAuthStateToPendoEvent(prev, curr);
        prev = curr;
        return event;
      }),
      filter((event): event is PendoEvents => event !== null),
    );
  });
}

function getIdentityFingerprint(state: AuthenticationState): string {
  return JSON.stringify({
    sessionId: state.session?.id,
    roles: state.session?.roles,
    title: state.session?.title,
    tenantId: state.session?.tenantId,
    csrId: state.impersonation?.csr?.id,
  });
}

function mapAuthStateToPendoEvent(
  prev: AuthenticationState | null,
  curr: AuthenticationState | null,
): PendoEvents | null {
  if (!curr) {
    return null;
  }

  // Logout
  if (curr.state === 'unauthenticated') {
    return prev?.session != null ? { type: 'USER_LOGGED_OUT' as const } : null;
  }

  // At this point, curr.state is 'authenticated' or 'unknown', so session should exist
  // But we need to guard against it being null
  if (!curr.session) {
    return null;
  }

  // Login (first time or after logout)
  if (!prev || prev.state === 'unauthenticated') {
    return {
      type: 'USER_LOGGED_IN' as const,
      params: {
        session: curr.session,
        impersonation: curr.impersonation,
      },
    };
  }

  // Session or impersonation changed (including roles, title, tenantId)
  if (getIdentityFingerprint(prev) !== getIdentityFingerprint(curr)) {
    return {
      type: 'USER_CONTEXT_CHANGED' as const,
      params: {
        session: curr.session,
        impersonation: curr.impersonation,
      },
    };
  }

  return null;
}
